import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useIntl } from 'umi';
import SelectMultiple from '@/pages/datalab/components/SelectMultiple';
import LineChart from '@customComp/Chart/components/LineChart';
import type { IMetricChartProps } from './types';
import {
  getYAxisScaleLimit,
  MAX_Y_AXIS_SPLIT_COUNT,
} from './utils';
import './index.scss';

const MetricChart: React.FC<IMetricChartProps> = ({
  table,
  isEn = false,
  isTranspose = false,
  selectedMetrics = [],
  onSelectedMetricsChange,
}) => {
  const { formatMessage } = useIntl();
  const [isChartCollapsed, setIsChartCollapsed] = useState<boolean>(false);
  // 建立“指标key -> 本地化显示名”映射，供图例和系列名复用。
  const metricNameMap = useMemo(() => {
    if (!table?.table_data?.length) {
      const emptyMap: Record<string, string> = {};
      return emptyMap;
    }
    return table.table_data.reduce<Record<string, string>>((acc, item) => {
      const metricInfo = item?.metric_name as {
        value?: string;
        value_cn?: string;
        value_en?: string;
      };
      const metricKey = metricInfo?.value || '';
      if (!metricKey || acc[metricKey]) {
        return acc;
      }
      acc[metricKey] = isEn
        ? metricInfo?.value_en || metricInfo?.value || metricKey
        : metricInfo?.value_cn || metricInfo?.value || metricKey;
      return acc;
    }, {});
  }, [table, isEn]);
  // 从明细数据里提取指标下拉选项并去重。
  const metricOptions = useMemo(() => {
    if (!table?.table_data?.length) {
      return [];
    }
    const metricOptions = table?.table_data?.reduce<{ value: string; value_type?: string; value_en?: string; value_cn?: string; text?: string; }[]>(
      (acc, item) => {
        const metricName = item?.metric_name as { value: string; value_cn?: string; value_en?: string; };
        const value = metricName?.value;
        if (typeof value !== 'string' || !value) {
          return acc;
        }
        const hasExists = acc.some((metric) => metric.value === value);
        if (!hasExists) {
          acc.push({
            value,
            value_type: item?.day1?.value_type as string,
            value_en: metricName?.value_en,
            value_cn: metricName?.value_cn,
            text: isEn ? metricName?.value_en : metricName?.value_cn,
          });
        }
        return acc;
      },
      [],
    );
    return metricOptions;
  }, [table, isEn]);

  // 规范化外部传入的 selectedMetrics，过滤无效值并提供兜底默认值。
  const selectedMetricKeys = useMemo(() => {
    if (!metricOptions?.length) {
      return [];
    }
    const validKeys = new Set(metricOptions.map((item) => item.value));
    const nextKeys = selectedMetrics.filter((key) => validKeys.has(key));
    if (nextKeys.length) {
      return nextKeys;
    }
    return metricOptions[0]?.value ? [metricOptions[0].value] : [];
  }, [metricOptions, selectedMetrics]);

  // 当有效选中值与外部状态不一致时，回写到父级 formState(chartConfig)。
  useEffect(() => {
    if (!onSelectedMetricsChange) {
      return;
    }
    const isSameLength = selectedMetricKeys.length === selectedMetrics.length;
    const isSameValue =
      isSameLength &&
      selectedMetricKeys.every((key, index) => key === selectedMetrics[index]);
    if (!isSameValue) {
      onSelectedMetricsChange(selectedMetricKeys);
    }
  }, [selectedMetricKeys, selectedMetrics, onSelectedMetricsChange]);

  // 自动识别观测天数字段（day1~dayN），并按天数排序。
  const dayKeyList = useMemo(() => {
    const firstRow = table?.table_data?.[0] || {};
    return Object.keys(firstRow)
      .filter((key) => /^day\d+$/i.test(key))
      .sort((a, b) => {
        const aOrder = Number(a.replace(/^day/i, '')) || 0;
        const bOrder = Number(b.replace(/^day/i, '')) || 0;
        return aOrder - bOrder;
      });
  }, [table]);

  // 从 column_name 中提取维度列（type=1），用于生成图例维度组合。
  const dimensionColumns = useMemo(() => {
    return (table?.column_name || []).filter((column) => column?.type === 1);
  }, [table]);

  // 将接口表格数据转换为 LineChart 所需结构（xAxis + series）。
  const chartData = useMemo(() => {
    const rows = table?.table_data || [];
    if (!rows.length || !dayKeyList.length || !selectedMetricKeys.length) {
      return {
        xAxisData: [],
        seriesData: [],
      };
    }
    const selectedSet = new Set(selectedMetricKeys);
    const isSingleSelectedMetric = selectedMetricKeys.length === 1;
    const seriesData = rows
      .filter((item) => {
        const metricValue = (item?.metric_name as { value?: string; })?.value || '';
        return selectedSet.has(metricValue);
      })
      .map((item) => {
        const metricValue = (item?.metric_name as { value?: string; })?.value || '';
        const day1ValueType = (item?.[dayKeyList[0]] as { value_type?: string; })?.value_type || 'number';
        // 多维度值按顺序拼接，作为系列维度标识。
        const dimensions = dimensionColumns
          .map((column) => {
            const field = item?.[column.origin_name] as {
              value?: string | null;
              value_cn?: string;
              value_en?: string;
            };
            const rawVal = isEn
              ? field?.value_en || field?.value
              : field?.value_cn || field?.value;
            const finalVal = rawVal ?? 'null';
            return String(finalVal);
          })
          .join(',');
        const metricText = metricNameMap[metricValue] || metricValue;
        let lineName = metricText;
        if (dimensions) {
          // 仅单指标时图例展示维度；多指标时补上指标名避免重名。
          lineName = isSingleSelectedMetric
            ? dimensions
            : `${dimensions},${metricText}`;
        }

        // day 字段展开为折线点位，空值保持为空串以便图表断点展示。
        const data = dayKeyList.map((dayKey) => {
          const dayValue = (item?.[dayKey] as { value?: string | number | null; })?.value;
          if (dayValue === null || dayValue === undefined || dayValue === '') {
            return '';
          }
          return dayValue;
        });
        return {
          data,
          metric: metricText,
          valueType: day1ValueType,
          dimensions,
          date_range: dayKeyList,
          last_time: '',
          is_compare: false,
          granularity: 'day',
          date_granularity: 'day',
          name: lineName,
          display_name: lineName,
          type: 'line',
          yAxisIndex: 0,
        };
      });
    return {
      xAxisData: dayKeyList.map((dayKey) => {
        const dayOrder = Number(dayKey.replace(/^day/i, '')) || 0;
        return isEn ? `Day ${dayOrder}` : `第${dayOrder}天`;
      }),
      seriesData,
    };
  }, [
    table,
    dayKeyList,
    selectedMetricKeys,
    dimensionColumns,
    isEn,
    metricNameMap,
  ]);
  const metricIsAllPercent = useMemo(() => {
    if (!chartData?.seriesData?.length) {
      return false;
    }
    return chartData.seriesData.every((item) => item.valueType === 'percent');
  }, [chartData]);

  return (
    <div className={classNames('dbl-right-context-line-chart', {
      'dbl-right-context-line-chart-collapsed': isChartCollapsed,
    })}>
      <div className="dbl-ab-experiment-metric-chart">
        <div className="dbl-ab-experiment-metric-chart-filter">
          <SelectMultiple
            label={formatMessage({ id: 'dbl_ab_experiment_metric_filter_title' })}
            isEn={isEn}
            minSelect={1}
            lastOneMessage={''}
            options={metricOptions}
            value={selectedMetricKeys}
            onClose={(value) => {
              let nextValue = value;
              if (!value.length) {
                nextValue = metricOptions[0]?.value
                  ? [metricOptions[0].value]
                  : [];
              }
              onSelectedMetricsChange?.(nextValue);
            }}
          />
        </div>
        <div className="dbl-ab-experiment-metric-chart-content">
          <LineChart
            // 表格转置会导致容器宽高变化，但 MetricChart 未必会触发 echarts resize；
            // 强制重建可确保展开后宽高立即生效。
            key={`dbl-ab-metric-linechart-${String(isTranspose)}`}
            isEvent={false}
            tooltipMinTopPx={-140}
            eventData={[]}
            chartData={chartData as any}
            metricNames={selectedMetricKeys}
            isOnlyOneDimension={false}
            metricIsAllPercent={metricIsAllPercent}
            chartConfig={{
              showDataLabel: true,
              displayForm: 'adaptive',
              labelContent: ['metric'],
            }}
            compareDateCount={0}
            fieldInfos={[]}
            setOtherConfigFunc={(opt: any) => {
              const newOptions = {
                ...opt,
                grid: {
                  ...(opt?.grid || {}),
                  right: 16,
                },
              };
              const tooltip = (newOptions as any)?.tooltip;
              const prevClassName =
                tooltip && typeof tooltip === 'object' ? String(tooltip?.className || '') : '';
              newOptions.tooltip = {
                ...(tooltip && typeof tooltip === 'object' ? tooltip : {}),
                className: `${prevClassName} dbl-ab-experiment-metric-chart-tooltip`,
              };
              const yAxisScale = getYAxisScaleLimit(
                (newOptions as any)?.series || [],
              );
              const yAxis = (newOptions as any)?.yAxis;
              if (Array.isArray(yAxis)) {
                newOptions.yAxis = yAxis.map((item, index) => {
                  if (index !== 0 || !item || typeof item !== 'object') {
                    return item;
                  }
                  return {
                    ...item,
                    // 强制最多 6 个刻度（含 0 轴）
                    splitNumber: MAX_Y_AXIS_SPLIT_COUNT,
                    ...(yAxisScale || {}),
                  };
                });
              } else if (yAxis && typeof yAxis === 'object') {
                newOptions.yAxis = {
                  ...yAxis,
                  // 强制最多 6 个刻度（含 0 轴）
                  splitNumber: MAX_Y_AXIS_SPLIT_COUNT,
                  ...(yAxisScale || {}),
                };
              }
              return newOptions;
            }}
          />
        </div>
      </div>
      <div
        className={classNames('dbl-expand-collapse-bottom', {
          'dbl-expand-collapse-bottom-open': !isChartCollapsed,
        })}
        onClick={() => {
          setIsChartCollapsed((prev) => !prev);
        }}
      >
        <div className='dbl-expand-collapse-init' />
      </div>
    </div>
  );
};

export default MetricChart;

